import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
} from 'firebase/firestore';

export default function DeveloperInboxPanel({ pageSize = 20 }) {
  const { isDev, loadingAuth } = useAuth();
  const [notes, setNotes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    if (!isDev) return; // safety
    setLoading(true);
    setError('');
    try {
      // Only show OPEN notes (resolved == false)
      const q = query(
        collection(db, 'devInbox'),
        where('resolved', '==', false),
        orderBy('createdAt', 'desc'), // may require index on first run
        limit(pageSize)
      );
      const snap = await getDocs(q);
      setNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(e?.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [isDev, pageSize]);

  React.useEffect(() => {
    if (!loadingAuth && isDev) load();
  }, [loadingAuth, isDev, load]);

  const toggleResolved = async (id, resolved) => {
    try {
      await updateDoc(doc(db, 'devInbox', id), { resolved: !resolved });
      setNotes(curr => curr.filter(n => n.id !== id)); // Remove from list after resolving
    } catch (e) {
      alert(e?.message || 'Update failed');
    }
  };

  if (loadingAuth || !isDev) return null; // Hide from non‑devs entirely

  return (
    <section className="bg-white border rounded p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-semibold">Developer Inbox</h3>
        <button onClick={load} className="text-blue-600 hover:underline">Refresh</button>
      </div>

      {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
      {loading ? (
        <div className="text-sm text-gray-600">Loading…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-gray-600">No notes yet.</div>
      ) : (
        <ul className="grid grid-cols-1 gap-2">
          {notes.map(n => {
            const created = n.createdAt?.toDate ? n.createdAt.toDate() : null;
            return (
              <li key={n.id} className="border rounded p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{n.subject || '(No subject)'}</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      From: {n.fromName || n.fromEmail || n.fromUid || 'Unknown'}
                      {created && ` • ${created.toLocaleString('en-GB')}`}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800">
                    Open
                  </span>
                </div>
                {n.message && (
                  <div className="text-sm whitespace-pre-wrap mt-2">{n.message}</div>
                )}

                {n.meta && (
                  <pre className="bg-gray-50 text-xs text-gray-700 p-2 rounded mt-2 overflow-auto">{JSON.stringify(n.meta, null, 2)}</pre>
                )}

                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => toggleResolved(n.id, n.resolved)}
                    className="px-3 py-1 rounded text-white bg-green-600 hover:bg-green-700"
                  >
                    Mark Resolved
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
